import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Action, Store } from '@ngrx/store';
import { Option } from '@sapphire/result';
import { exhaustMap, map, Observable, of, tap } from 'rxjs';

import { EffectsBase } from '@ps/core-data-access';
import { ToastService } from '@ps/core-overlay';
import { ProjectService } from '@ps/survey-data-access';
import { TemplateSelectorsFacade } from '@ps/survey-template-data-access';
import {
  findTemplateLocation,
  resolveTrackerParent,
  Survey,
  SURVEY_MESSAGES,
  TEMPLATE_MESSAGES,
  TrackerConfig,
  trackerConfigOfProject,
  TrackerTemplateProjectPayloadInput,
} from '@ps/survey-utils';
import { Nullable } from '@ps/utils';

import {
  SurveyCreationTrackerActions,
  SurveyCreationTrackerApiActions,
} from './survey-creation-audience.actions';
import {
  selectCoreChildSurveys,
  selectRootSurvey,
  selectTrackerConfig,
} from '../core/survey-creation-core.selectors';

@Injectable()
export class SurveyCreationTrackerTemplateEffects extends EffectsBase {
  private readonly _actions$ = inject(Actions);
  private readonly _store = inject(Store);
  private readonly _projectService = inject(ProjectService);
  private readonly _templateSelectors = inject(TemplateSelectorsFacade);
  private readonly _toastService = inject(ToastService);

  // A template on a tracker builds a new tracker project; the endpoint fills in
  // every wave from the template.
  public readonly createTrackerFromTemplate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(SurveyCreationTrackerActions.createTrackerFromTemplate),
      concatLatestFrom(() => [
        this._store.select(selectRootSurvey),
        this._store.select(selectTrackerConfig),
        this._store.select(selectCoreChildSurveys),
      ]),
      exhaustMap(
        ([
          { templateId, replaceTracker },
          rootSurvey,
          preDraftConfig,
          childSurveys,
        ]) =>
          this._templateInput(templateId, rootSurvey, preDraftConfig).match({
            some: (input) =>
              this._create(
                input,
                replaceTracker ? childSurveys.map(({ id }) => id) : [],
              ),
            none: () => this._unavailable(),
          }),
      ),
    ),
  );

  private _templateInput(
    templateId: number,
    rootSurvey: Nullable<Survey>,
    preDraftConfig: Nullable<TrackerConfig>,
  ): Option<TrackerTemplateProjectPayloadInput> {
    const localization = findTemplateLocation(
      this._templateSelectors.templates(),
      templateId,
    ).andThen(({ template }) => Option.from(template.localization));

    return this._trackerConfig(rootSurvey, preDraftConfig).andThen(
      (trackerConfig) =>
        localization.map((locale) => ({
          templateId,
          localization: locale,
          trackerConfig,
        })),
    );
  }

  // An existing tracker keeps its own settings; a new one takes those chosen for it.
  private _trackerConfig(
    rootSurvey: Nullable<Survey>,
    preDraftConfig: Nullable<TrackerConfig>,
  ): Option<TrackerConfig> {
    return Option.from(rootSurvey)
      .andThen((root) => Option.from(resolveTrackerParent(root)))
      .map(trackerConfigOfProject)
      .orElse(() => Option.from(preDraftConfig));
  }

  private _create(
    input: TrackerTemplateProjectPayloadInput,
    replacedSurveyIds: number[],
  ): Observable<Action> {
    return this._projectService.createTrackerProjectFromTemplate(input).pipe(
      tap(() => this._toastService.success(SURVEY_MESSAGES.SAVED)),
      map((survey) =>
        SurveyCreationTrackerApiActions.createTrackerFromTemplateSuccess({
          survey,
          replacedSurveyIds,
        }),
      ),
      this.psCatchError(
        (error) =>
          of(
            SurveyCreationTrackerApiActions.createTrackerFromTemplateFailure({
              error: error.message,
            }),
          ),
        { notify: true },
      ),
    );
  }

  private _unavailable(): Observable<Action> {
    this._toastService.error(TEMPLATE_MESSAGES.TRACKER_UNAVAILABLE);
    return of(
      SurveyCreationTrackerApiActions.createTrackerFromTemplateFailure({
        error: TEMPLATE_MESSAGES.TRACKER_UNAVAILABLE,
      }),
    );
  }
}
